import { StateCreator } from 'zustand';
import { Dayjs } from 'dayjs';
import { DEFAULT_PAGE_SIZE } from '../../../components/molecules/dataDisplay/dataGrid';

export type Filter = StringFilter | NumberFilter | DateRangeFilter | StringArrayFilter;
export type StringFilter = FilterGeneral<string>;
export type StringArrayFilter = FilterGeneral<string[]>;
export type NumberFilter = FilterGeneral<number>;
export type DateRangeFilter = FilterGeneral<{ from?: Dayjs; to?: Dayjs; timezone: string }>;
export type FilterGeneral<T> = {
  key: string;
  value: T;
  // Value prepared to be rendered on the UI (mostly for the filters Chips)
  valueView?: string | string[];
};
export type FiltersCollection = Record<string, Filter>;

export function isDateRangeFilter(filter: Filter): filter is DateRangeFilter {
  return typeof filter.value === 'object' && 'from' in filter.value;
}

export type SortingModel = { field: string; sort: 'asc' | 'desc' }[];
export type PaginationModel = { page: number; size: number };

export type DefaultFilterState = {
  activeFilters: FiltersCollection;
  sortingModel: SortingModel;
  paginationModel: PaginationModel;
  setSortingModel: (model: SortingModel) => void;
  setPaginationModel: (model: PaginationModel) => void;
  setFilter: (filter: Filter) => void;
  clearFilter: (key: string) => void;
  clearFilters: () => void;
  getFilter: <T>(key: string) => FilterGeneral<T> | null;
  hasFilter: (key: string) => boolean;
  setFilterFromOptions: (key: string, value: string[], options: { value: string; label: string }[]) => void;
};

export const createDefaultFilterSlice: StateCreator<DefaultFilterState, [], [], DefaultFilterState> = (set, get) => ({
  activeFilters: {},
  sortingModel: [],
  paginationModel: { page: 1, size: DEFAULT_PAGE_SIZE },
  setSortingModel: (model) => {
    set({
      sortingModel: model,
    });
  },
  setPaginationModel: (model) => {
    set({
      paginationModel: model,
    });
  },
  setFilter: (filter) => {
    set((state) => {
      // Empty array, removing filter
      if (Array.isArray(filter.value) && filter.value.length === 0) {
        get().clearFilter(filter.key);
        return { activeFilters: state.activeFilters };
      }
      // Empty string, removing filter
      if (typeof filter.value === 'string' && filter.value.length === 0) {
        get().clearFilter(filter.key);
        return { activeFilters: state.activeFilters };
      }

      return {
        activeFilters: {
          ...state.activeFilters,
          [filter.key]: filter,
        },
      };
    });
  },
  clearFilter: (key) => {
    set((state) => ({
      activeFilters: Object.keys(state.activeFilters).reduce<Record<string, Filter>>((acc, filterIndex) => {
        if (filterIndex !== key) {
          acc[filterIndex] = state.activeFilters[filterIndex];
        }
        return acc;
      }, {}),
    }));
  },
  hasFilter: (key) => {
    const filter = get().activeFilters[key];
    return !!filter && (Array.isArray(filter.value) ? filter.value.length > 0 : !!filter.value);
  },
  clearFilters: () => {
    set(() => ({
      activeFilters: {},
    }));
  },
  getFilter: <T>(key: string) => {
    const filter = get().activeFilters[key] as T | undefined;
    return filter || null;
  },
  setFilterFromOptions: (key: string, value: string[], options: { value: string; label: string }[]) => {
    get().setFilter({
      key,
      valueView: value
        .map((l) => options.find((s) => s.value === l)?.label)
        .filter(Boolean)
        .join(', '),
      value,
    });
  },
});
